/**
 * Unified API Client
 *
 * Provides a single interface for vb-server API calls.
 * This is the recommended way to access APIs in V2 components.
 *
 * @example
 * import { UnifiedAPI } from '@/api/unified-api';
 *
 * const results = await UnifiedAPI.search({ org_id, q: 'emergency' });
 * const alerts = await UnifiedAPI.getAlerts(org_id);
 */

import { Search, EditOrganizationChannel, EditInvitation, Alert } from '../common/entities';
import { logger } from '../utils/logger';

// Import vb-server API modules
import * as serverOrgs from './server/orgs';
import * as serverChannels from './server/channels';
import * as serverAlerts from './server/alerts';
import * as serverTags from './server/tags';
import * as serverSpeakers from './server/speakers';
import * as serverInvites from './server/invites';
import * as serverUsers from './server/users';
import * as serverSearch from './server/search';
import * as serverAnalytics from './server/analytics';

/**
 * Log backend selection (once per session)
 */
let backendLogged = false;
function logBackend() {
  if (!backendLogged && import.meta.env.DEV) {
    logger.info('[UnifiedAPI] Using vb-server backend');
    backendLogged = true;
  }
}

/**
 * Transform array to object keyed by ID (for backward compat with legacy format)
 */
function arrayToObject<T extends { [key: string]: any }>(
  items: T[],
  idKey: string
): Record<string, T> {
  const result: Record<string, T> = {};
  for (const item of items) {
    result[item[idKey]] = item;
  }
  return result;
}

/**
 * Unified API client for vb-server
 */
export const UnifiedAPI = {
  /**
   * Search messages
   */
  async search(body: any, signal?: AbortSignal): Promise<Search> {
    logBackend();
    const response = await serverSearch.searchMessagesOpenSearch({
      orgId: body.org_id,
      query: body.q || '',
      channelIds: body.channels?.split(',').filter(Boolean),
      startTime: body.created_start,
      endTime: body.created_end,
      limit: body.count || body.size || 50
    });
    return response as unknown as Search;
  },

  /**
   * Get aggregations only (optimized for analytics)
   */
  async getAggregations(
    orgId: string,
    options: {
      channelIds?: string[];
      startTime?: number;
      endTime?: number;
      aggs?: string;
    } = {},
    signal?: AbortSignal
  ): Promise<Search> {
    logBackend();
    // Fetch analytics data from vb-server
    const [volumeData, heatmapData] = await Promise.all([
      serverAnalytics.getVolume({
        orgId,
        channelIds: options.channelIds,
        startTime: options.startTime,
        endTime: options.endTime
      }),
      serverAnalytics.getHeatmap({
        orgId,
        channelIds: options.channelIds,
        startTime: options.startTime,
        endTime: options.endTime
      })
    ]);

    // Transform to OpenSearch-compatible aggregations format
    return {
      hits: { total: { value: 0 }, hits: [] },
      aggregations: {
        messages_over_time: {
          buckets: (volumeData || []).map((v) => ({
            key_as_string: v.time,
            key: new Date(v.time).getTime(),
            doc_count: v.count
          }))
        },
        day_and_hour_aggs: {
          buckets: (heatmapData || []).map((h) => ({
            key: h.hw_id,
            doc_count: h.doc_count || 0
          }))
        }
      }
    } as unknown as Search;
  },

  /**
   * Get organization with all extras (channels, users, alerts, tags, speakers, invites)
   */
  async getOrganization(org_id: string): Promise<any> {
    logBackend();
    // Fetch org and all extras in parallel
    const [org, channels, users, alerts, tags, speakers, invites] = await Promise.all([
      serverOrgs.getOrg(org_id),
      serverChannels.listChannels(org_id),
      serverUsers.listOrgUsers(org_id),
      serverAlerts.listAlerts(org_id),
      serverTags.listTags(org_id),
      serverSpeakers.listSpeakers(org_id),
      serverInvites.listInvites(org_id)
    ]);

    // Combine into legacy format
    return {
      ...org,
      org_id,
      channels: arrayToObject(channels, 'channel_id'),
      users: arrayToObject(users, 'user_id'),
      alerts: arrayToObject(alerts, 'alert_id'),
      tags: arrayToObject(tags, 'tag_id'),
      speakers: arrayToObject(speakers, 'speaker_id'),
      invites: arrayToObject(invites, 'invite_id')
    };
  },

  /**
   * Get channels for organization
   */
  async getChannels(org_id: string): Promise<EditOrganizationChannel> {
    logBackend();
    const channels = await serverChannels.listChannels(org_id);
    return arrayToObject(channels, 'channel_id') as unknown as EditOrganizationChannel;
  },

  /**
   * Get alerts for organization
   */
  async getAlerts(org_id: string): Promise<Record<string, Alert>> {
    logBackend();
    const alerts = await serverAlerts.listAlerts(org_id);
    return arrayToObject(alerts, 'alert_id');
  },

  /**
   * Get tags for organization
   */
  async getTags(org_id: string): Promise<Record<string, unknown>> {
    logBackend();
    const tags = await serverTags.listTags(org_id);
    return arrayToObject(tags, 'tag_id');
  },

  /**
   * Get invitations for organization
   */
  async getInvitations(org_id: string): Promise<EditInvitation> {
    logBackend();
    const invites = await serverInvites.listInvites(org_id);
    return arrayToObject(invites, 'invite_id') as unknown as EditInvitation;
  },

  /**
   * Get speakers for organization
   */
  async getSpeakers(org_id: string): Promise<any> {
    logBackend();
    const speakers = await serverSpeakers.listSpeakers(org_id);
    return arrayToObject(speakers, 'speaker_id');
  },

  /**
   * Get users for organization
   */
  async getUsers(org_id: string): Promise<any> {
    logBackend();
    const users = await serverUsers.listOrgUsers(org_id);
    return arrayToObject(users, 'user_id');
  },

  /**
   * Health check for backend
   */
  async healthCheck(): Promise<{ backend: string; healthy: boolean; details?: any }> {
    const result = await serverOrgs.healthCheck();
    return { backend: 'vb-server', healthy: result.healthy, details: result };
  },

  /**
   * Get active backend name
   */
  getActiveBackend(): 'vb-server' {
    return 'vb-server';
  }
};

export default UnifiedAPI;
